import React, { useState, useEffect } from 'react';
import {
    Spin, Button, Card, Tag, message, Tooltip, Input, Modal, Space, Table
} from 'antd';
import {
    EditOutlined,
    StopOutlined,
    CheckOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import Fuse from 'fuse.js';
import _ from 'lodash';
import moment from 'moment';
import * as Service from '../core/Service';
import NavAndSideFrame from '../components/NavAndSideFrame';
import OrderForm from '../components/OrderForm';
import { useTranslation } from 'react-i18next';
import { displayIsActive, displayMoment, displayOrderStatus, displayOrderType } from '../components/table/DisplayMethod';

const title = `訂單列表`;
const selectedKey = `order_list`;
const tableIdName = `order_id`;
const filterableField = [
    'order_date',
    'start_address',
    'end_address',
    'call_sign'
];
const DEFAULT_PAGE_SIZE = 25;

const OrderList = (props) => {
    const {t} = useTranslation();

    const [dataList, setDataList] = useState([]);
    const [unChangeData, setUnChangeData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState({});
    const [isChangingIsActive, setChangingIsActive] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getAllData();
    }, []);

    const getAllData = async () => {
        let endpoint = `/api/order/list`;
        let list = [];
        setLoading(true);
        try {
            let data = await Service.call(`GET`, endpoint);
            list = data;
            if (data.errorMessage) list = [];
        } catch (error) {
            console.log(error);
        } finally {
            setDataList(list);
            setUnChangeData(list);
            setLoading(false);
        }
    }

    const setTableHeader = () => {
        let columns = [
            {
                title: '操作',
                dataIndex: tableIdName,
                width: 120,
                fixed: 'left',
                render: (value, record) => {
                    return (
                        <span>
                            <Tooltip title={t('common.edit')}>
                                <Button
                                    shape="circle"
                                    style={{ marginRight: 8, color: '#000000' }}
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setSelectedRecord(record);
                                        setModalVisible(true);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={record.is_active ? t('order_list.disable_order') : t('order_list.enable_order')}>
                                <Button
                                    shape="circle"
                                    style={{ color: record.is_active ? 'red' : 'green' }}
                                    icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                                    onClick={async () => {
                                        if (isChangingIsActive) return; // avoid multiple attempts

                                        setChangingIsActive(true);
                                        let { order_id, is_active } = record;
                                        is_active = !is_active;

                                        let result = await Service.call('put', `/api/order`, { order_id, is_active });
                                        setChangingIsActive(false);

                                        if (_.isEmpty(result) || result.status < 1) {
                                            return message.error(is_active ? t('order_list.enable_failed') : t('order_list.disable_failed'));
                                        }
                                        await getAllData();
                                        return message.success(is_active ? t('order_list.enabled_order') : t('order_list.disabled_order'));
                                    }}
                                />
                            </Tooltip>
                        </span>
                    )
                }
            },
            { title: t('vehicle.call_sign'), dataIndex: 'call_sign', width: 120 },
            { title: t('order_list.order_id'), dataIndex: tableIdName, width: 100 },
            {
                title: t('common.is_active'),
                dataIndex: 'is_active',
                width: 100,
                render: (value) => {
                    return displayIsActive(value, t);
                }
            },
            {
                title: t('common.status'),
                dataIndex: `status`,
                width: 120,
                render: (value) => {
                    return displayOrderStatus(value, t);
                },
            },
            { 
              title: t('order_list.order_type'), 
              dataIndex: `order_type`,
              width: 120,
              render: (value) => {
                return displayOrderType(value, t);
              },
            },
            { title: t('order_list.order_date'), dataIndex: `order_date`, width: 120 },
            {
                title: t('order_list.order_time'),
                dataIndex: `order_time`,
                width: 120,
                render: (value) => {
                    return displayMoment(value);
                }
            },
            { title: t('order_list.start_address'), dataIndex: `start_address`, width: 200},
            { title: t('order_list.end_address'), dataIndex: `end_address`, width: 200},
            { title: t('order_list.customer_appkey'), dataIndex: `customer_appkey`, width: 150 },
            { title: t('order_list.customer_contact_number'), dataIndex: `customer_contact_number`, width: 180 },
            { title: t('order_list.tunnel_id'), dataIndex: `tunnel_id`, width: 100 },
            { title: t('order_list.tips'), dataIndex: `tips`, width: 100 },
            { title: t('order_list.requested_seat_number'), dataIndex: `requested_seat_number`, width: 120 },
            { title: t('order_list.pets'), dataIndex: `pets`, width: 80 },
            { title: t('order_list.call_fee'), dataIndex: `call_fee`, width: 100 },
            { title: t('order_list.speak_in_english'), dataIndex: `speak_in_english`, width: 120 },
            { title: t('driver.driver_id'), dataIndex: `driver_id`, width: 100 },
            { title: t('vehicle.vehicle_id'), dataIndex: `vehicle_id`, width: 100 },
            { title: t('common.start_time'), dataIndex: `start_time`, width: 150 },
            { title: t('common.end_time'), dataIndex: `end_time`, width: 150 },
            {
                title: t('common.ctime'),
                dataIndex: 'ctime',
                width: 150,
                render: (value) => {
                    return displayMoment(value);
                }
            },
            {
                title: t('common.utime'),
                dataIndex: 'utime',
                width: 150,
                render: (value) => {
                    return displayMoment(value);
                }
            },
            { title: t('common.remarks'), dataIndex: `remarks`, width: 200 },
            { title: t('order_list.recreated_order_id'), dataIndex: `recreated_order_id`, width: 150 },
            { title: t('order_list.translate_start_address'), dataIndex: `translated_start_address`, width: 200},
            { title: t('order_list.translate_end_address'), dataIndex: `translated_end_address`, width: 200 },
        ];
        return columns;
    }

    const getDataBySearchKeyword = (keywords) => {
        let dataList = [];
        try {
            const options = {
                includeScore: true,
                findAllMatches: true,
                threshold: 0.15,
                keys: filterableField
            }
            const fuse = new Fuse(unChangeData, options)
            const result = fuse.search(keywords);
            console.log("RESULT", result);
            dataList = _.map(result, 'item');
            dataList = _.orderBy(dataList, [`ctime`], [`desc`]);
        } catch (error) {
            console.error('error >>> ', error);
        } finally {
            setDataList(dataList);
        }
    }

    let timeout;
    const debounce = (func, delay) => {
        clearTimeout(timeout);
        timeout = setTimeout(func, delay);
    };

    const onKeywordChange = (keyword) => {
        debounce(() => {
            getDataBySearchKeyword(keyword);
            if (_.isEmpty(keyword)) setDataList(unChangeData);
        }, 400);
    };

    return (
        <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
            <Card>
                <Space direction="horizontal">
                    <>以文字搜尋:</>
                    <Input
                        placeholder="請輸入文字"
                        onChange={(values) => {
                            onKeywordChange(values.target.value);
                        }}
                    />
                </Space>
            </Card>
            <Card>
                <Spin spinning={loading}>
                    <Table
                        pagination={{
                            defaultPageSize: DEFAULT_PAGE_SIZE,
                            showSizeChanger: true,
                            pageSizeOptions: [DEFAULT_PAGE_SIZE, 50, 100, 200],
                        }}
                        scroll={{ 
                            x: 'max-content',
                        }}
                        dataSource={dataList}
                        columns={setTableHeader()}
                        rowKey={tableIdName}
                        size="small"
                        bordered
                    />
                </Spin>
            </Card>
            <Modal
                title={t('order_list.order')}
                visible={modalVisible}
                footer={null}
                style={{ maxWidth: 800 }}
                width="90%"
                onCancel={() => { setModalVisible(false) }}
            >
                <OrderForm
                    selectedRecord={selectedRecord}
                    openModal={(value) => {
                        getAllData();
                        setModalVisible(value);
                    }}
                />
            </Modal>

        </NavAndSideFrame>
    );
}

export default OrderList;
